<template>
  <div :id="`project-${project.id}`" class="rounded-md">
    <div class="bg-white">
      <ExpansionPanel v-model="isExpanded" class="">
        <template #activator="{ toggle, isShown }">
          <div
            class="border-r border-l border-mid-neutral-50 sticky top-0 bg-white z-10"
            :class="{ 'border-b': !isShown }"
          >
            <div
              class="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 py-2 px-[24px] sm:flex-nowrap hover:bg-light-neutral-300"
            >
              <div
                class="flex justify-between items-center cursor-pointer transition-all duration-200 w-96"
                :class="[isShown ? 'text-black/90  ' : 'text-black/90  ']"
                @click="toggle"
              >
                <div class="flex gap-2 items-center">
                  <button @click.stop="toggle">
                    <div
                      v-if="isShown"
                      class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
                    >
                      <ChevronUpIcon class="stroke-2 hover:brightness-125 text-mid-neutral-500" />
                    </div>
                    <div
                      v-else
                      class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
                    >
                      <ChevronDownIcon class="stroke-2 hover:brightness-125 text-mid-neutral-500" />
                    </div>
                  </button>
                  <p class="font-semibold text-dark-neutral-500" :class="[isShown ? '' : '']">
                    {{ project.name }}
                  </p>
                </div>
              </div>
              <div class="flex gap-x-2 items-center w-32">
                <div
                  class="bg-tertiary-50 size-7 text-tertiary-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
                >
                  <DocumentTextIcon class="size-5" aria-hidden="true" />
                </div>
                <p class="font-medium text-dark-neutral-500">
                  {{ project.reviewSeries?.length }} Review{{
                    project.reviewSeries?.length > 1 ? "s" : ""
                  }}
                </p>
              </div>
              <div class="flex gap-x-2 items-center w-72">
                <div class="flex -space-x-1">
                  <div v-if="project.users.length === 0">
                    <div
                      class="size-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 font-medium ring-2 ring-white"
                    >
                      <p class="text-xs font-medium">N/A</p>
                    </div>
                  </div>
                  <div v-for="projectUser in project.users" v-else :key="projectUser.id">
                    <div
                      class="size-7 flex items-center justify-center bg-primary-50 text-primary-500 rounded-full font-medium ring-2 ring-white"
                    >
                      <p class="text-xs font-medium">
                        {{
                          (
                            projectUser.firstName.charAt(0) + projectUser.lastName.charAt(0)
                          ).toUpperCase()
                        }}
                      </p>
                    </div>
                  </div>
                </div>
                <p class="font-medium text-dark-neutral-500">
                  {{ project?.users.length }} Member{{ project?.users.length > 1 ? "s" : "" }}
                </p>
              </div>

              <div class="flex items-center gap-2">
                <ProjectAuditLogsModal :project-id="project.id!" :users="users" v-if="isAdmin">
                  <template #activator="{ show }">
                    <button
                      class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
                      title="View activity log"
                      @click.stop="show"
                    >
                      <ClockIcon
                        class="w-5 h-5 stroke-mid-neutral-500 hover:stroke-mid-neutral-700"
                      />
                    </button>
                  </template>
                </ProjectAuditLogsModal>
                <button
                  v-if="isAdmin"
                  class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
                  @click.stop="toggleFavorite()"
                >
                  <StarIcon
                    class="w-5 h-5"
                    :class="[
                      project.isFavorite
                        ? 'fill-yellow-400 stroke-yellow-400'
                        : 'stroke-mid-neutral-500 fill-transparent',
                    ]"
                  />
                </button>
                <ProjectItemActions :project="project" />
              </div>
            </div>
          </div>
        </template>
        <template #default>
          <div
            v-if="(project.reviewSeries?.length ?? 0) > 0"
            class="border-b border-r border-l border-mid-neutral-50"
          >
            <Sortable
              :items="project.reviewSeries"
              class=""
              @item:moved="
                ({ item: series, fractionalIndex }) => moveReviewSeries(series.id, fractionalIndex)
              "
            >
              <template #item="{ item: series, onDragEnd, onDragStart }">
                <div class="group flex items-start w-full">
                  <div class="invisible group-hover:visible">
                    <CreateProjectReview
                      :project-id="project.id!"
                      :after-review-series-id="series.id"
                    >
                      <template #activator="{ show }">
                        <FloatingMenu
                          :offset="[10, -14]"
                          :arrow="false"
                          :placement="'bottom-start'"
                          class="pl-7 group-hover:bg-gray-50"
                        >
                          <template #activator="{ show: showFloatingMenu }">
                            <button
                              class="py-3 text-secondary text-mid-neutral-500"
                              @click.stop="showFloatingMenu"
                              v-if="isAdmin"
                            >
                              <PlusIcon class="size-5" />
                            </button>
                          </template>
                          <template #content>
                            <div class="w-[200px]">
                              <button
                                class="flex gap-2 px-5 py-3 items-center"
                                @click.stop="showCreateReviewDrawer(true, show)"
                              >
                                <CpuChipIcon
                                  class="size-6 text-mid-neutral-300"
                                  aria-hidden="true"
                                />
                                <span
                                  class="hover:cursor-pointer text-sm font-medium leading-5 text-dark-neutral-500"
                                >
                                  Automated
                                </span>
                              </button>
                              <button
                                class="flex gap-2 px-5 py-3 items-center"
                                @click.stop="showCreateReviewDrawer(false, show)"
                              >
                                <HandRaisedIcon class="size-6 text-mid-neutral-300" />
                                <span
                                  class="hover:cursor-pointer text-sm font-medium leading-5 text-dark-neutral-500"
                                >
                                  Manual
                                </span>
                              </button>
                            </div>
                          </template>
                        </FloatingMenu>
                      </template>
                    </CreateProjectReview>
                  </div>
                  <ReviewSeries
                    :project="project"
                    :review-series="series"
                    :on-drag-end="onDragEnd"
                    :on-drag-start="onDragStart"
                    class="flex-1"
                  />
                </div>
              </template>
            </Sortable>
          </div>

          <div
            v-if="project.reviewSeries?.length === 0"
            class="border-b border-r border-l border-mid-neutral-50"
          >
            <CreateProjectReview
              v-if="isAdmin"
              :project-id="project.id!"
              class="h-10 pl-20 w-full"
            />
          </div>
        </template>
      </ExpansionPanel>
    </div>
  </div>
</template>

<script lang="ts" setup>
import StarIcon from "@app/components/Icons/StarIcon.vue";
import CreateProjectReview from "@app/views/Projects/Project/CreateReview/CreateProjectReview.vue";
import { Project } from "@core/domain/models/project.model";
import { computed } from "vue";
import useProjects from "@app/views/Projects/use-projects";
import { User } from "@core/domain/models/user.model";
import ChevronDownIcon from "@app/components/Icons/ChevronDownIcon.vue";
import ChevronUpIcon from "@app/components/Icons/ChevronUpIcon.vue";
import { Role } from "@core/domain/types/role.type";
import ExpansionPanel from "@app/components/Global/ExpansionPanel.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { AuthKey } from "@app/injectionKeys";
import ProjectItemActions from "./ProjectPlan/ProjectSettings/ProjectItemActions.vue";
import { DocumentTextIcon } from "@heroicons/vue/24/solid";
import ReviewSeries from "./ReviewSeries/ReviewSeries.vue";
import Sortable from "@app/components/Global/Sortable/Sortable.vue";
import useLoading from "@app/composables/use-loading";
import { ClockIcon, CpuChipIcon, HandRaisedIcon, PlusIcon } from "@heroicons/vue/24/outline";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import useCreateProjectReview from "./CreateReview/useCreateProjectReview";
import ProjectAuditLogsModal from "../ProjectAuditLogsModal.vue";

const props = defineProps<{
  project: Project;
  users: User[];
}>();

const isExpanded = defineModel<boolean>({
  default: false,
});

const loading = useLoading();
const projects = useProjects();
const { user } = injectStrict(AuthKey);

function toggleFavorite() {
  projects.toggleFavorite(props.project.id!);
}

async function moveReviewSeries(reviewSeriesId: string, order: string) {
  loading.start();
  try {
    await projects.moveReviewSeries(reviewSeriesId, order);
  } finally {
    loading.stop();
  }
}
const { isReviewAutomated } = useCreateProjectReview(props.project.id!);
function showCreateReviewDrawer(isAutomated: boolean, cb: () => void) {
  isReviewAutomated.value = isAutomated;
  cb();
}

const isAdmin = computed(() => user.value?.role === Role.ADMIN);
</script>
